import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Loader2 } from "lucide-react";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "web-design-system";
import { APIError, handleError } from "web-core/utils";
import { useCreatePortfolioReview } from "creso-core/hooks";
import { PortfolioReviewSource } from "creso-core/types";
import { REVIEW_SOURCE_OPTIONS } from "../constants";

export function StartReviewMenu({
  clientId,
  investmentAccountId,
}: {
  clientId: number;
  investmentAccountId: string;
}) {
  const navigate = useNavigate();
  const { mutate: createReview, isPending } = useCreatePortfolioReview();

  const openReview = (reviewId: number | string) =>
    navigate({
      to: "/clients/$id/review/$reviewId",
      params: { id: String(clientId), reviewId: String(reviewId) },
    });

  const start = (source: PortfolioReviewSource) =>
    createReview(
      { investment_account_guid: investmentAccountId, mode: "MANUAL", source },
      {
        onSuccess: (review) => openReview(review.id),
        onError: (error) => {
          // One draft per client: reopen it instead of failing.
          const details = (error as APIError).data?.details as
            | { review_id?: string }
            | undefined;
          if (
            error instanceof APIError &&
            error.data?.code === "DRAFT_REVIEW_EXISTS" &&
            details?.review_id
          ) {
            openReview(details.review_id);
            return;
          }
          handleError(error, "Could not start the review");
        },
      }
    );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button disabled={isPending || !investmentAccountId}>
          Start Review
          {isPending ? <Loader2 className="animate-spin" /> : <ChevronDown />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {REVIEW_SOURCE_OPTIONS.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onSelect={() => start(option.value)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
